import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Tooltip,
  IconButton,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Refresh as RefreshIcon,
  AutoAwesome as SparkleIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';
import api from '../shared/services/api';
import { DEFAULT_CONFIDENCE, atlasToMlField, ML_TO_ATLAS_HEADER_MAPPING, ML_TO_ATLAS_POSITION_MAPPING } from './mlPredictionCategories';
import { isMlBlackoutActive, isApplyingPredictions } from './mlCoordination';
import { parseMultiValue, sanitizeMlResponse } from './mlPredictionSanitize';
import codelistService from '../services/codelistService';

interface MLPredictionCellProps {
  fieldPath: string;
  fieldValue: string;
  extractedData: any;
  onApplyPrediction: (prediction: string) => void;
  onPredictionShown?: (fieldPath: string, value: string) => void;
  overrideSource?: string;  // "exact_context" | "partial_context" | "field_preference" | undefined
}

interface FieldPrediction {
  value: string;
  confidence: number;
  source: 'ml' | 'cache' | 'learned';
  alternatives?: Array<{
    value: string;
    confidence: number;
  }>;
}

// ============================================================================
// HELPER: Trim ML values (e.g., "0.0" -> "0", remove unnecessary decimals)
// ============================================================================
const trimMLValue = (value: string): string => {
  if (!value) return value;
  
  // If it's a numeric value, format it properly
  const numValue = parseFloat(value);
  if (!isNaN(numValue)) {
    // For whole numbers, remove decimal points
    if (numValue === Math.floor(numValue)) {
      return numValue.toString();
    }
    // For decimals, keep reasonable precision
    return numValue.toString();
  }
  
  // For non-numeric values, return as-is
  return value;
};

// ============================================================================
// AUDIT FIX: Shared Prediction Cache to prevent duplicate API calls
// ============================================================================

interface PredictionCache {
  data: any;
  timestamp: number;
  dataHash: string;
}

interface PendingRequest {
  promise: Promise<any>;
  dataHash: string;
}

// Module-level cache and pending request tracker
let predictionCache: PredictionCache | null = null;
let pendingRequest: PendingRequest | null = null;
const CACHE_TTL_MS = 10000; // 10 seconds cache TTL

/**
 * Generate a simple hash of the extracted data for cache key comparison
 */
function generateDataHash(data: any): string {
  const str = JSON.stringify(data);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return String(hash);
}

/**
 * Check if cache is valid for given data
 */
function isCacheValid(dataHash: string): boolean {
  if (!predictionCache) return false;
  const now = Date.now();
  const isExpired = (now - predictionCache.timestamp) > CACHE_TTL_MS;
  const dataChanged = predictionCache.dataHash !== dataHash;
  return !isExpired && !dataChanged;
}

/**
 * Get or fetch predictions with request deduplication
 * AUDIT FIX: Multiple components share a single API request
 */
async function getSharedPredictions(headerData: any, positionsData: any[]): Promise<any> {
  // NUCLEAR OPTION: Check BOTH the flag AND the blackout timer
  // This ensures NO API calls within 5 seconds of Apply All
  const isBlackoutActive = isMlBlackoutActive();

  if (isApplyingPredictions() || isBlackoutActive) {
    console.log('[MLPredictionCell] ⛔ API blocked - blackout active or Apply All in progress');
    // Return cached data if available, otherwise return empty result
    if (predictionCache) {
      return predictionCache.data;
    }
    return { success: false, header: {}, positions: [], message: 'Blackout active' };
  }

  const dataHash = generateDataHash({ header: headerData, positions: positionsData });

  // Return cached data if valid
  if (isCacheValid(dataHash)) {
    console.debug('[MLPredictionCell] Using cached predictions');
    return predictionCache!.data;
  }

  // If there's already a pending request for the same data, wait for it
  if (pendingRequest && pendingRequest.dataHash === dataHash) {
    console.debug('[MLPredictionCell] Waiting for pending request');
    return pendingRequest.promise;
  }

  // Create new request
  console.debug('[MLPredictionCell] Starting new prediction request');
  // Use low threshold (5%) since V5 model with field masking has valid predictions at lower confidence
  const mlRequest = {
    header: headerData,
    positions: positionsData,
    confidence_threshold: 0.05,
    return_top_k: 3  // Request top 3 predictions for each field
  };

  const requestPromise = api.post('/api/v1/atlas-ml/predict/impute', mlRequest, {
    timeout: 30000
  }).then(response => {
    // Sanitize before caching: the model invents identity fields (EORIs, TINs,
    // names) and re-guesses fields the user's settings own. Filtering here
    // covers every consumer of the cache at once.
    response.data = sanitizeMlResponse(response.data);
    if (response.data.success) {
      // Cache the result
      predictionCache = {
        data: response.data,
        timestamp: Date.now(),
        dataHash
      };
      console.debug('[MLPredictionCell] Cached new predictions');
    }
    pendingRequest = null;
    return response.data;
  }).catch(error => {
    pendingRequest = null;
    throw error;
  });

  pendingRequest = { promise: requestPromise, dataHash };
  return requestPromise;
}

/**
 * Clear the prediction cache (call when data changes significantly)
 */
export function clearPredictionCache(): void {
  predictionCache = null;
  pendingRequest = null;
}

/**
 * Flatten nested ATLAS v2.1 structure to flat ML training format
 * ML model expects: { empfng_name: '', ausfuh_land: '' }
 * Form provides: { empfanger: { name: '' }, ausfuhrer: { land: '' } }
 *
 * AUDIT FIX (2025-12-24): Added comprehensive flattening for all 465+ ML fields
 * FIX (2026-01-12): Convert empty values to [NULL] so ML API calculates confidence scores
 */

/**
 * Convert empty/null/undefined values to [NULL] for ML API
 * The ML API only calculates confidence scores for fields with value "[NULL]"
 * Empty strings ('') are NOT treated as missing and won't get predictions!
 */
const toNull = (val: any): string => {
  if (val === null || val === undefined || val === '') {
    return '[NULL]';
  }
  return String(val);
};

const flattenAtlasToMlFormat = (kopfData: any): Record<string, any> => {
  if (!kopfData || typeof kopfData !== 'object') return {};

  const flat: Record<string, any> = {};

  // Copy any flat fields directly
  Object.keys(kopfData).forEach(key => {
    if (typeof kopfData[key] !== 'object' || kopfData[key] === null) {
      flat[key] = kopfData[key];
    }
  });

  // ========== EMPFANGER (Recipient) ==========
  if (kopfData.empfanger) {
    flat.empfng_name = toNull(kopfData.empfanger.name);
    flat.empfng_tin = toNull(kopfData.empfanger.tin);
    flat.empfng_eori = toNull(kopfData.empfanger.eori);
    flat.empfng_land = toNull(kopfData.empfanger.land);
    flat.empfng_stra_hnum = toNull(kopfData.empfanger.strasse);
    flat.empfng_plz_ort = toNull(kopfData.empfanger.plz);
    flat.empfng_ort = toNull(kopfData.empfanger.ort);
    flat.empfng_niederlassung = toNull(kopfData.empfanger.niederlassungsNummer);
  }

  // ========== AUSFUHRER (Exporter) ==========
  if (kopfData.ausfuhrer) {
    flat.ausfuh_name = toNull(kopfData.ausfuhrer.name);
    flat.ausfuh_tin = toNull(kopfData.ausfuhrer.tin);
    flat.ausfuh_eori = toNull(kopfData.ausfuhrer.eori);
    flat.ausfuh_land = toNull(kopfData.ausfuhrer.land);
    flat.ausfuh_stra_hnum = toNull(kopfData.ausfuhrer.strasse);
    flat.ausfuh_plz_ort = toNull(kopfData.ausfuhrer.plz);
    flat.ausfuh_ort = toNull(kopfData.ausfuhrer.ort);
    flat.ausfuh_email = toNull(kopfData.ausfuhrer.email);
    flat.ausfuh_telefon = toNull(kopfData.ausfuhrer.telefon);
  }

  // ========== ANMELDER (Declarant) ==========
  if (kopfData.anmelder) {
    flat.anmeld_name = toNull(kopfData.anmelder.name);
    flat.anmeld_tin = toNull(kopfData.anmelder.tin);
    flat.anmeld_eori = toNull(kopfData.anmelder.eori);
    flat.anmeld_land = toNull(kopfData.anmelder.land);
    flat.anmeld_stra_hnum = toNull(kopfData.anmelder.strasse);
    flat.anmeld_plz_ort = toNull(kopfData.anmelder.plz);
    flat.anmeld_ort = toNull(kopfData.anmelder.ort);
    flat.anmeld_telefon = toNull(kopfData.anmelder.phone || kopfData.anmelder.telefon);
    flat.anmeld_email = toNull(kopfData.anmelder.email);
    flat.anmeld_ansprech_sachb = toNull(kopfData.anmelder.ansprechpartner);
  }

  // ========== VERTRETER (Representative) ==========
  if (kopfData.vertreter) {
    flat.vertreter_tin = toNull(kopfData.vertreter.tin);
    flat.vertreter_niederlassung = toNull(kopfData.vertreter.niederlassungsNummer);
    flat.vertreter_name = toNull(kopfData.vertreter.name);
    flat.vertreter_land = toNull(kopfData.vertreter.land);
    flat.vertreter_stra = toNull(kopfData.vertreter.strasse);
    flat.vertreter_plz = toNull(kopfData.vertreter.plz);
    flat.vertreter_ort = toNull(kopfData.vertreter.ort);
  }

  // ========== SUBUNTERNEHMER (Subcontractor) - AUDIT FIX: Was missing ==========
  if (kopfData.subunternehmer) {
    flat.subunt_tin = toNull(kopfData.subunternehmer.tin);
    flat.subunt_nam = toNull(kopfData.subunternehmer.name);
    flat.subunt_land = toNull(kopfData.subunternehmer.land);
    flat.subunt_stra = toNull(kopfData.subunternehmer.strasse);
    flat.subunt_plz = toNull(kopfData.subunternehmer.plz);
    flat.subunt_ort = toNull(kopfData.subunternehmer.ort);
  }

  // ========== VERSENDER (Sender) - AUDIT FIX: Was missing ==========
  if (kopfData.versender) {
    flat.versender_tin = toNull(kopfData.versender.tin);
    flat.versender_name = toNull(kopfData.versender.name);
    flat.versender_land = toNull(kopfData.versender.land);
    flat.versender_stra = toNull(kopfData.versender.strasse);
    flat.versender_plz = toNull(kopfData.versender.plz);
    flat.versender_ort = toNull(kopfData.versender.ort);
  }

  // ========== ENDVERWERDER (End User) - AUDIT FIX: Was missing ==========
  if (kopfData.endverwerder) {
    flat.endverwerder_tin = toNull(kopfData.endverwerder.tin);
    flat.endverwerder_name = toNull(kopfData.endverwerder.name);
    flat.endverwerder_land = toNull(kopfData.endverwerder.land);
    flat.endverwerder_stra = toNull(kopfData.endverwerder.strasse);
    flat.endverwerder_plz = toNull(kopfData.endverwerder.plz);
    flat.endverwerder_ort = toNull(kopfData.endverwerder.ort);
  }

  // ========== BEFORDERER (Carrier) - AUDIT FIX: Was missing ==========
  if (kopfData.beforderer) {
    flat.beforderer_tin = toNull(kopfData.beforderer.tin);
    flat.beforderer_name = toNull(kopfData.beforderer.name);
    flat.beforderer_land = toNull(kopfData.beforderer.land);
  }

  // ========== LIEFERUNG (Delivery Terms) ==========
  if (kopfData.lieferung) {
    flat.ausfuhr_land = toNull(kopfData.lieferung.ausfuhrLand);
    flat.bestimmungsland = toNull(kopfData.lieferung.bestimmungsland || kopfData.lieferung.bestimmungsLand);
    flat.art_des_geschafts = toNull(kopfData.lieferung.artdesGeschafts);
    flat.lieferbed_incoterm = toNull(kopfData.lieferung.lieferbedingungIncotermCode);
    flat.lieferbed_ort = toNull(kopfData.lieferung.lieferbedingungOrt);
    flat.lieferbed_land = toNull(kopfData.lieferung.lieferbedingungLand);
  }

  // ========== SENDUNG (Shipment) ==========
  if (kopfData.sendung) {
    flat.anzahl_pakete = toNull(kopfData.sendung.anzahlpakete);
    flat.gesamt_gewicht = toNull(kopfData.sendung.gesamtgewicht);
    flat.inl_verkehrszweig = toNull(kopfData.sendung.inlandischerVerkehrszweig);
    flat.grenz_verkehrszweig = toNull(kopfData.sendung.verkehrszweigAnDerGrenze);
  }

  // ========== BEFORDERUNGSMITTEL (Transport Means) - AUDIT FIX: Was missing ==========
  if (kopfData.beforderungsmittel) {
    flat.beford_an_der_gren_art = toNull(kopfData.beforderungsmittel.artAnDerGrenze);
    flat.beford_an_der_gren_kz = toNull(kopfData.beforderungsmittel.kennzeichenAnDerGrenze);
    flat.beford_an_der_gren_staat = toNull(kopfData.beforderungsmittel.staatAnDerGrenze);
    flat.beford_am_abgang_art = toNull(kopfData.beforderungsmittel.artAmAbgang);
    flat.beford_am_abgang_kenn = toNull(kopfData.beforderungsmittel.kennzeichenAmAbgang);
  }

  // ========== BEFORDERUNGSROUTE (Transport Route) - AUDIT FIX: Was missing ==========
  if (kopfData.beforderungsroute) {
    flat.bef_route_land = toNull(kopfData.beforderungsroute.land);
  }

  // ========== WARENORT (Location of Goods) - AUDIT FIX: Was missing ==========
  if (kopfData.warenort) {
    flat.warenort_art = toNull(kopfData.warenort.art);
    flat.warenort_kennz = toNull(kopfData.warenort.kennzeichen);
    flat.warenort_land = toNull(kopfData.warenort.land);
  }

  // ========== BEWILLIGUNG (Authorization) - AUDIT FIX: Was missing ==========
  if (kopfData.bewilligung) {
    flat.bewill_art = toNull(kopfData.bewilligung.art);
    flat.bewill_ref = toNull(kopfData.bewilligung.referenz);
  }

  // ========== Top-level KOPF fields ==========
  flat.kopf_gesamt_rohmasse = toNull(kopfData.gesamtRohmasse);
  flat.kopf_in_rechnung_gestellt = toNull(kopfData.inRechnunggestellterGesamtbetrag);
  flat.rechnungswahrung = toNull(kopfData.rechnungswahrung);
  flat.dienst_no = toNull(kopfData.lrn);
  flat.kopf_art_der_aus = toNull(kopfData.artderAnmeldung);
  flat.kopf_zeit_der_anmeld = toNull(kopfData.zeitpunktderAnmeldung);
  flat.ausfuhr_zollstelle = toNull(kopfData.ausfuhrzollstelleDienststellennummer);
  flat.ausgang_zollstelle = toNull(kopfData.vorgeseheneAusgangszollstelleDienststellennummer);
  flat.massgebl_datum = toNull(kopfData.massgeblichesDatum);
  flat.versionsnr = toNull(kopfData.versionsnr);
  flat.eori_niederlassungsnr = toNull(kopfData.eoriNiederlassungsnummer);
  flat.dienststellennr = toNull(kopfData.dienststellennummer);
  flat.sicherheit = toNull(kopfData.sicherheit);
  flat.besondere_umstande = toNull(kopfData.besondereUmstande);

  return flat;
};

/**
 * Flatten position data to ML format
 * AUDIT FIX (2025-12-24): Handles both flat ATLAS structure and nested structure
 *
 * ATLAS structure can be:
 * 1. Flat (correct): { wareWarennummerKN8: 'xxx', wareEigenmasse: 1.0 }
 * 2. Nested (legacy): { ware: { warennummer: 'xxx', eigenmasse: 1.0 } }
 */
const flattenPositionToMlFormat = (position: any): Record<string, any> => {
  if (!position || typeof position !== 'object') return {};

  const flat: Record<string, any> = {};

  // Copy flat fields directly (excludes nested objects)
  Object.keys(position).forEach(key => {
    if (typeof position[key] !== 'object' || position[key] === null) {
      flat[key] = position[key];
    }
  });

  // ========== Handle FLAT ATLAS structure (correct per spec) ==========
  // Map flat ATLAS field names to ML field names
  // Use toNull() to convert empty values to [NULL] for ML API confidence calculation
  flat.ware_warenum_kn8 = toNull(position.wareWarennummerKN8 || position.warennummer);
  flat.ware_warenbe = toNull(position.wareWarenbezeichnung || position.warenbezeichnung);
  flat.ware_egmasse = toNull(position.wareEigenmasse || position.eigenmasse);
  flat.ware_rohmasse = toNull(position.wareRohmasse || position.rohmasse);
  flat.ware_stat_wert = toNull(position.statistischerWert);
  flat.ware_cusnr = toNull(position.warecusnummer || position.cusnummer);
  flat.ware_tarifnr = toNull(position.wareTarifnummer || position.tarifnummer);
  flat.ware_menge = toNull(position.menge);
  flat.ware_mengeneinheit = toNull(position.mengeneinheit);
  flat.ware_ursprungsbland = toNull(position.ursprungsland);
  flat.ware_positionsnr = toNull(position.warePositionsnummer || position.positionsnummer);
  flat.praferenz_code = toNull(position.praferenzCode);
  flat.zusatz_zollnr = toNull(position.zusatzlicheZollnummer);
  flat.ref_ucr = toNull(position.referenznummerUCR);
  flat.reg_extern = toNull(position.registriernummerextern);
  flat.verfahren = toNull(position.verfahren);
  flat.zusatz_verfahren = toNull(position.zusatzlichesVerfahren);

  // ========== Handle NESTED structure (legacy fallback) ==========
  // Only apply nested fallback if flat value is [NULL] (meaning it was empty)
  if (position.ware) {
    if (flat.ware_warenum_kn8 === '[NULL]') {
      flat.ware_warenum_kn8 = toNull(position.ware.warennummer || position.ware.wareWarennummerKN8);
    }
    if (flat.ware_warenbe === '[NULL]') {
      flat.ware_warenbe = toNull(position.ware.warenbezeichnung || position.ware.wareWarenbezeichnung);
    }
    if (flat.ware_egmasse === '[NULL]') {
      flat.ware_egmasse = toNull(position.ware.eigenmasse || position.ware.eigenMasse);
    }
    if (flat.ware_rohmasse === '[NULL]') {
      flat.ware_rohmasse = toNull(position.ware.rohmasse || position.ware.rohMasse);
    }
    if (flat.ware_stat_wert === '[NULL]') {
      flat.ware_stat_wert = toNull(position.ware.statistischerWert);
    }
    if (flat.ware_cusnr === '[NULL]') {
      flat.ware_cusnr = toNull(position.ware.cusnummer || position.ware.warecusnummer);
    }
    if (flat.ware_tarifnr === '[NULL]') {
      flat.ware_tarifnr = toNull(position.ware.tarifnummer || position.ware.wareTarifnummer);
    }
    if (flat.ware_menge === '[NULL]') {
      flat.ware_menge = toNull(position.ware.menge);
    }
    if (flat.ware_mengeneinheit === '[NULL]') {
      flat.ware_mengeneinheit = toNull(position.ware.mengeneinheit);
    }
    if (flat.ware_ursprungsbland === '[NULL]') {
      flat.ware_ursprungsbland = toNull(position.ware.ursprungsland);
    }
  }

  // ========== VERPACKUNG (Packaging) ==========
  if (position.verpackung) {
    // Handle array of packaging or single object
    const verpack = Array.isArray(position.verpackung) ? position.verpackung[0] : position.verpackung;
    if (verpack) {
      flat.verpack_art = toNull(verpack.artderVerpackung || verpack.art);
      flat.verpack_anzahl = toNull(verpack.anzahlderPackstucke || verpack.anzahl);
      flat.versandzeichen = toNull(verpack.versandzeichen);
    }
  }

  // ========== UNTERLAGE (Supporting Documents) - AUDIT FIX: Was missing ==========
  if (position.unterlage) {
    const unterlage = Array.isArray(position.unterlage) ? position.unterlage[0] : position.unterlage;
    if (unterlage) {
      flat.unterlage_typ = toNull(unterlage.typ);
      flat.unterlage_ref = toNull(unterlage.referenz);
      flat.unterlage_datum = toNull(unterlage.datum);
    }
  }

  // ========== SONSTIGER VERWEIS (Other References) - AUDIT FIX: Was missing ==========
  if (position.sonstigerVerweis) {
    const verweis = Array.isArray(position.sonstigerVerweis) ? position.sonstigerVerweis[0] : position.sonstigerVerweis;
    if (verweis) {
      flat.sonst_verweis_typ = toNull(verweis.typ);
      flat.sonst_verweis_ref = toNull(verweis.referenz);
    }
  }

  // ========== CONTAINER - AUDIT FIX: Was missing ==========
  if (position.container) {
    flat.container_nr = toNull(position.container.nummer);
    flat.container_art = toNull(position.container.art);
  }

  return flat;
};

// ============================================================================
// Manual Entry Field Detection
// These fields require manual entry (not predicted by ML model)
// Includes: price/value/amount fields AND high-cardinality fields (HS codes)
// ============================================================================
const MANUAL_ENTRY_PATTERNS = [
  // Price/value/amount fields - vary per document
  /preis/i,           // price fields
  /wert$/i,           // value fields ending in 'wert' (statistischerwert, etc.)
  /betrag/i,          // amount fields
  /gesamtpreis/i,     // total price
  /statistisch/i,     // statistical fields
  // High-cardinality fields - too many unique values (>1000) for ML vocab
  /warennummer/i,     // All product/HS code fields
  /warennr/i,         // HS code variants
  /taric/i,           // TARIC code fields
  /kn8/i,             // KN8 code fields
  /codenummer/i,      // Code number fields
  /ursprungsland/i,   // Origin country (position level - sparse in training data)
];

/**
 * Check if a field requires manual entry (not predicted by ML model)
 * Includes price/value fields AND high-cardinality fields like HS codes
 */
const isManualEntryField = (fieldPath: string): boolean => {
  const fieldName = fieldPath.split('.').pop() || fieldPath;
  // Also check the full path for nested fields
  return MANUAL_ENTRY_PATTERNS.some(pattern =>
    pattern.test(fieldName) || pattern.test(fieldPath)
  );
};

const MLPredictionCell: React.FC<MLPredictionCellProps> = ({
  fieldPath,
  fieldValue,
  extractedData,
  onApplyPrediction,
  onPredictionShown,
  overrideSource,
}) => {
  const theme = useTheme();
  const [prediction, setPrediction] = useState<FieldPrediction | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noPredictionAvailable, setNoPredictionAvailable] = useState(false); // NEW: Track when no prediction exists
  const [requiresManualEntry, setRequiresManualEntry] = useState(false); // Fields that ML intentionally skips (includes HS codes)
  const [displayValue, setDisplayValue] = useState<string>(''); // For codelist-resolved display
  const [isResolvingCodelist, setIsResolvingCodelist] = useState(false);
  const { token } = useAuthStore();
  const mountedRef = useRef(true);
  // TEMPORARY: Track if initial prediction was already made - prevent refetching on field changes
  const initialPredictionMadeRef = useRef(false);
  // Note: abortControllerRef removed - shared cache handles request deduplication

  const generatePrediction = useCallback(async () => {
    // TEMPORARY: Skip prediction if already made for this field (disable reload on field changes)
    if (initialPredictionMadeRef.current) {
      console.log('[MLPredictionCell] ⛔ Skipping prediction - initial prediction already made for:', fieldPath);
      return;
    }
    if (!token || !extractedData) {
      console.log('[MLPrediction] Missing token or extractedData:', { hasToken: !!token, hasData: !!extractedData });
      return;
    }

    setLoading(true);
    setError(null);
    setNoPredictionAvailable(false); // Reset on new attempt

    // Check if this field requires manual entry (price/value fields AND HS codes skipped by ML)
    const manualEntry = isManualEntryField(fieldPath);
    console.log('[MLPrediction] Field check:', { fieldPath, isManualEntry: manualEntry, fieldName: fieldPath.split('.').pop() });
    if (manualEntry) {
      console.log('[MLPrediction] Setting requiresManualEntry=true for:', fieldPath);
      setRequiresManualEntry(true);
      setLoading(false);
      return;
    }

    try {
      // Convert extracted data to the flat format expected by ML API
      // ML model was trained on flat field names like empfng_name, ausfuh_land
      let headerData: Record<string, any> = {};
      let positionsData: Record<string, any>[] = [];

      // DEBUG: Log the structure of extractedData
      console.log('[MLPrediction] extractedData structure:', {
        hasKopf: !!extractedData?.kopf,
        hasHeader: !!extractedData?.header,
        topLevelKeys: Object.keys(extractedData || {}).slice(0, 10)
      });

      if (extractedData?.kopf) {
        // ATLAS v2.1 format - flatten nested structure
        headerData = flattenAtlasToMlFormat(extractedData.kopf);
        const rawPositions = extractedData.positionen || extractedData.position || [];
        positionsData = rawPositions.map((pos: any) => flattenPositionToMlFormat(pos));
        console.log('[MLPrediction] Using kopf structure, flattened fields:', Object.keys(headerData).length);
      } else if (extractedData?.header) {
        // Already in header/positions format - might need flattening
        headerData = flattenAtlasToMlFormat(extractedData.header);
        const rawPositions = extractedData.positions || [];
        positionsData = rawPositions.map((pos: any) => flattenPositionToMlFormat(pos));
        console.log('[MLPrediction] Using header structure, flattened fields:', Object.keys(headerData).length);
      } else {
        // Fallback to entire extracted data as header
        headerData = flattenAtlasToMlFormat(extractedData);
        positionsData = [];
        console.log('[MLPrediction] Using fallback structure, flattened fields:', Object.keys(headerData).length);
      }

      // AUDIT FIX: Use shared prediction cache to prevent duplicate API calls
      // Multiple MLPredictionCell components will share a single request
      const responseData = await getSharedPredictions(headerData, positionsData);

      // DEBUG: Log response structure
      console.log('[MLPrediction] API Response:', {
        success: responseData.success,
        headerFieldCount: Object.keys(responseData.header || {}).length,
        sampleFields: Object.keys(responseData.header || {}).slice(0, 5),
        hasKopfZeitDerAnmeld: !!responseData.header?.kopf_zeit_der_anmeld
      });

      // Check if component is still mounted before updating state
      if (!mountedRef.current) return;

      if (responseData.success) {
        // Find prediction for this specific field
        console.log('[MLPrediction] Looking up field:', fieldPath);
        const fieldPrediction = findFieldPrediction(
          responseData,
          fieldPath
        );

        console.log('[MLPrediction] Field prediction result:', fieldPrediction);

        if (fieldPrediction) {
          // Determine source: check if this field had a user-learned override
          // override_sources keys use ML field names, so convert our fieldPath
          const overrideSources = responseData.override_sources || {};
          const fieldName = fieldPath.replace(/^kopf\./, '').replace(/positionen\[\d+\]\./, '');
          const isPositionField = fieldPath.includes('positionen[') || fieldPath.includes('position[');
          const mlName = atlasToMlField(fieldName, isPositionField);
          const isOverride = !!(mlName && overrideSources[mlName]) || !!overrideSource;
          const predSource = isOverride ? 'learned' : (predictionCache ? 'cache' : 'ml');

          setPrediction({
            value: fieldPrediction.value,
            confidence: fieldPrediction.confidence,
            source: predSource
          });

          // Notify parent about the prediction shown (for correction tracking)
          if (onPredictionShown) {
            onPredictionShown(fieldPath, fieldPrediction.value);
          }
        } else {
          console.log('[MLPrediction] No prediction found for field:', fieldPath);
          // Set flag to show user that prediction was attempted but no value available
          setNoPredictionAvailable(true);
        }
      }
    } catch (err: any) {
      if (!mountedRef.current) return;
      // Ignore aborted requests (they're intentional)
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        return;
      }
      console.error('[MLPrediction] Error:', err);
      setError(err.response?.data?.detail || 'Prediction failed');
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        // TEMPORARY: Mark initial prediction as made to prevent refetching on field changes
        initialPredictionMadeRef.current = true;
      }
    }
  }, [token, extractedData, fieldPath]);

  // Auto-predict when field is empty or null (with debouncing to prevent rapid API calls)
  useEffect(() => {
    // NUCLEAR OPTION: Check BOTH the flag AND the blackout timer
    // This ensures NO auto-predict within 5 seconds of Apply All
    const isBlackoutActive = isMlBlackoutActive();

    if (isApplyingPredictions() || isBlackoutActive) {
      console.log('[MLPredictionCell] ⛔ Auto-predict blocked by blackout');
      return;
    }

    if (!fieldValue || fieldValue === '' || fieldValue === '[NULL]') {
      // Debounce for 500ms to prevent rapid API calls during typing
      const debounceTimer = setTimeout(() => {
        // Double-check BOTH conditions before actually calling (in case they changed during debounce)
        const stillBlackout = isMlBlackoutActive();
        if (!isApplyingPredictions() && !stillBlackout) {
          generatePrediction();
        } else {
          console.log('[MLPredictionCell] ⛔ Auto-predict cancelled - blackout still active after debounce');
        }
      }, 500);

      return () => clearTimeout(debounceTimer);
    }
  }, [fieldValue, generatePrediction]);

  // Resolve codelist value when prediction changes
  useEffect(() => {
    if (!prediction?.value || !fieldPath) {
      setDisplayValue(prediction?.value || '');
      return;
    }

    const resolveCodelistValue = async () => {
      console.log('[MLPredictionCell] Resolving codelist for field:', fieldPath, 'with value:', prediction.value);
      
      try {
        setIsResolvingCodelist(true);

        // Multi-value ($-delimited list, e.g. "$DE$US$") → readable "DE, US".
        if (typeof prediction.value === 'string' && prediction.value.includes('$')) {
          setDisplayValue(parseMultiValue(prediction.value).join(', '));
          return;
        }

        // Get codelist ID for this field
        const codelistId = codelistService.getCodelistIdForField(fieldPath);
        console.log('[MLPredictionCell] Codelist ID:', codelistId, 'for field:', fieldPath);
        
        if (!codelistId) {
          // No codelist for this field, use raw value
          console.log('[MLPredictionCell] No codelist mapping for field:', fieldPath);
          setDisplayValue(prediction.value);
          return;
        }

        // Fetch the codelist
        const codelist = await codelistService.getCodelist(codelistId);
        console.log('[MLPredictionCell] Codelist fetched:', codelist?.id, 'with', codelist?.values?.length, 'values');
        
        if (!codelist || !codelist.values) {
          console.log('[MLPredictionCell] No codelist data found for ID:', codelistId);
          setDisplayValue(prediction.value);
          return;
        }

        // Find the matching value
        const matchingValue = codelist.values.find(
          v => v.code === prediction.value || v.code.toLowerCase() === prediction.value.toLowerCase()
        );

        if (matchingValue) {
          console.log('[MLPredictionCell] Found match:', matchingValue.code, '-', matchingValue.description);
          
          // Smart truncation for very long descriptions
          if (matchingValue.description.length > 30) {
            // For very long descriptions, show first 2-3 words
            const words = matchingValue.description.split(' ').slice(0, 2).join(' ');
            setDisplayValue(`${matchingValue.code} - ${words}...`);
            console.log('[MLPredictionCell] Truncated to:', `${matchingValue.code} - ${words}...`);
          } else {
            setDisplayValue(`${matchingValue.code} - ${matchingValue.description}`);
          }
        } else {
          // Try best match
          console.log('[MLPredictionCell] No exact match for value:', prediction.value);
          const bestMatch = codelistService.findBestMatch(prediction.value, codelist);
          if (bestMatch) {
            console.log('[MLPredictionCell] Found best match:', bestMatch.code, '-', bestMatch.description);
            if (bestMatch.description.length > 30) {
              const words = bestMatch.description.split(' ').slice(0, 2).join(' ');
              setDisplayValue(`${bestMatch.code} - ${words}...`);
            } else {
              setDisplayValue(`${bestMatch.code} - ${bestMatch.description}`);
            }
          } else {
            console.log('[MLPredictionCell] No match found, using raw value');
            setDisplayValue(prediction.value);
          }
        }
      } catch (error) {
        console.error('[MLPredictionCell] Error resolving codelist:', error);
        setDisplayValue(prediction.value);
      } finally {
        setIsResolvingCodelist(false);
      }
    };

    resolveCodelistValue();
  }, [prediction?.value, fieldPath]);

  // Cleanup on unmount to prevent memory leaks
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Note: Shared cache handles request cleanup - no need for abortController
    };
  }, []);

  const findFieldPrediction = (mlResponse: any, fieldPath: string) => {
    // AUDIT FIX: Use centralized mlPredictionCategories.ts mappings (324+ fields)
    const logFieldLookup = (message: string, details?: any) => {
      console.log(`[MLPrediction:findField] ${message}`, details || '');
    };

    // Check if this is a position field (format: positionen[index].fieldName or position[index].fieldName)
    const positionMatch = fieldPath.match(/(?:positionen|position)\[(\d+)\]\.(.+)/);
    if (positionMatch) {
      const positionIndex = parseInt(positionMatch[1], 10);
      const positionField = positionMatch[2];

      // Get the position from ML response
      const mlPosition = mlResponse.positions?.[positionIndex];
      if (!mlPosition) {
        logFieldLookup(`Position ${positionIndex} not found in ML response`, { fieldPath, availablePositions: mlResponse.positions?.length || 0 });
        return null;
      }

      // Use centralized mapping to find ML field name
      const mlFieldName = atlasToMlField(positionField, true);
      if (mlFieldName) {
        const value = mlPosition[mlFieldName];
        const confidenceKey = `position_${positionIndex}.${mlFieldName}`;
        const confidence = mlResponse.confidence_scores?.[confidenceKey] || DEFAULT_CONFIDENCE;

        // Check for multiple predictions in all_predictions
        const allPredictionsKey = `position[${positionIndex}].${mlFieldName}`;
        const alternatives = mlResponse.all_predictions?.[allPredictionsKey];

        if (value && value !== '[UNK]' && value !== '[NULL]' && value !== '' && confidence > 0) {
          logFieldLookup(`Found position prediction via centralized mapping`, { fieldPath, mlField: mlFieldName, value, confidence, alternatives: alternatives?.length });
          return { 
            value, 
            confidence,
            alternatives: alternatives?.slice(1) // Skip first as it's the main value
          };
        }
      }

      // Fallback: Try direct field name in ML response
      const directValue = mlPosition[positionField];
      if (directValue && directValue !== '[UNK]' && directValue !== '[NULL]' && directValue !== '') {
        logFieldLookup(`Found position prediction via direct field name`, { fieldPath, value: directValue });
        return { value: directValue, confidence: DEFAULT_CONFIDENCE };
      }

      logFieldLookup(`No prediction found for position field`, { fieldPath, positionField, mlFieldName });
      return null;
    }

    // Header field lookup using centralized mapping
    const mlFieldName = atlasToMlField(fieldPath, false);

    if (mlFieldName) {
      // Look in header section of ML response
      const value = mlResponse.header?.[mlFieldName];
      const confidenceKey = `header.${mlFieldName}`;
      const confidence = mlResponse.confidence_scores?.[confidenceKey] || DEFAULT_CONFIDENCE;
      
      // Check for multiple predictions in all_predictions
      const allPredictionsKey = `header.${mlFieldName}`;
      const alternatives = mlResponse.all_predictions?.[allPredictionsKey];

      if (value && value !== '[UNK]' && value !== '[NULL]' && value !== '' && confidence > 0) {
        logFieldLookup(`Found header prediction via centralized mapping`, { fieldPath, mlField: mlFieldName, value, confidence, alternatives: alternatives?.length });
        return { 
          value, 
          confidence,
          alternatives: alternatives?.slice(1) // Skip first as it's the main value
        };
      }
    }

    // Fallback: Try without kopf prefix (e.g., kopf.empfanger.name -> empfanger.name)
    const withoutKopf = fieldPath.replace(/^kopf\./, '');
    if (withoutKopf !== fieldPath) {
      const mlFieldNameFallback = atlasToMlField(withoutKopf, false);
      if (mlFieldNameFallback) {
        const value = mlResponse.header?.[mlFieldNameFallback];
        if (value && value !== '[UNK]' && value !== '[NULL]' && value !== '') {
          logFieldLookup(`Found header prediction via fallback mapping`, { fieldPath, mlField: mlFieldNameFallback, value });
          return { value, confidence: DEFAULT_CONFIDENCE };
        }
      }
    }

    // Final fallback: Try direct field name lookup in header
    const directFieldName = fieldPath.split('.').pop() || fieldPath;
    const directValue = mlResponse.header?.[directFieldName];
    if (directValue && directValue !== '[UNK]' && directValue !== '[NULL]' && directValue !== '') {
      logFieldLookup(`Found header prediction via direct field name`, { fieldPath, field: directFieldName, value: directValue });
      return { value: directValue, confidence: DEFAULT_CONFIDENCE };
    }

    logFieldLookup(`No prediction found for header field`, { fieldPath, mlFieldName });
    return null;
  };

  const handleApplyPrediction = () => {
    if (prediction && onApplyPrediction) {
      onApplyPrediction(trimMLValue(prediction.value));
      // Keep the prediction displayed but show it as applied
    }
  };

  // Don't show predictions for fields that already have values
  if (fieldValue && fieldValue !== '' && fieldValue !== '[NULL]') {
    return null;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">
          Predicting...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
        <Tooltip title={error}>
          <IconButton size="small" onClick={generatePrediction}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography variant="caption" color="error">
          Failed
        </Typography>
      </Box>
    );
  }

  // Price/value/amount fields that ML intentionally skips — render nothing
  if (requiresManualEntry) {
    return null;
  }

  // No ML prediction available — render nothing
  if (noPredictionAvailable && !prediction) {
    return null;
  }

  // IMPORTANT: Check for manual entry fields BEFORE showing Predict button
  // This ensures HS codes and other high-cardinality fields never show the button
  if (!prediction && isManualEntryField(fieldPath)) {
    return null;
  }

  if (!prediction) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<SparkleIcon sx={{ fontSize: 14 }} />}
          onClick={generatePrediction}
          sx={{
            fontSize: '0.65rem',
            height: 22,
            borderRadius: 11,
            minWidth: 0,
            px: 1.5,
            textTransform: 'none',
            borderColor: 'primary.main',
          }}
        >
          Predict
        </Button>
      </Box>
    );
  }

  const isLearned = prediction.source === 'learned';
  const confidencePct = prediction.confidence * 100;
  const isHighConfidence = confidencePct > 90;

  const borderColor = (isLearned || isHighConfidence)
    ? alpha('#4caf50', 0.5)
    : alpha(theme.palette.primary.main, 0.35);
  const bgColor = (isLearned || isHighConfidence)
    ? alpha('#4caf50', 0.18)
    : alpha(theme.palette.primary.main, 0.14);
  const accentColor = (isLearned || isHighConfidence) ? '#388e3c' : theme.palette.primary.main;

  return (
    <Tooltip title={`${prediction.value}${displayValue !== prediction.value ? ` (${displayValue})` : ''}${isLearned ? ' (Learned from your corrections)' : ''}`}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        width: '100%',
        bgcolor: bgColor,
        border: '1px solid',
        borderColor: borderColor,
        borderRadius: '6px',
        px: 1,
        py: '4px',
      }}>
        <SparkleIcon sx={{ fontSize: 13, color: 'text.primary', flexShrink: 0, opacity: 0.85 }} />
        <Typography
          sx={{
            fontSize: '0.75rem',
            fontWeight: 500,
            color: 'text.primary',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {trimMLValue(displayValue || prediction.value)}
        </Typography>
        <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
          AI
        </Typography>
        <Button
          variant="contained"
          size="small"
          onClick={handleApplyPrediction}
          sx={{
            height: 22,
            fontSize: '0.65rem',
            borderRadius: 11,
            minWidth: 0,
            px: 1.5,
            textTransform: 'none',
            backgroundColor: accentColor,
            color: '#fff',
            boxShadow: 'none',
            lineHeight: 1,
            '&:hover': {
              backgroundColor: accentColor,
              opacity: 0.88,
              boxShadow: 'none',
            },
          }}
        >
          Apply
        </Button>
      </Box>
    </Tooltip>
  );
};

export default MLPredictionCell;